"use client";

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type AddExerciseToRoutineInput,
  type AddExerciseToWorkoutInput,
  type AddSetInput,
  // Routines
  addExerciseToRoutine,
  // Workout
  addExerciseToWorkout,
  // Sets
  addSet,
  type BodyMeasurementItem,
  type CalendarDay,
  type CompleteSetResult,
  type CreateCustomExerciseInput,
  type CreateRoutineInput,
  completeSet,
  completeWorkout,
  // Exercises
  createCustomExercise,
  createRoutine,
  createRoutineFolder,
  type DashboardStats,
  deleteCustomExercise,
  // Body measurements
  deleteMeasurement,
  deleteRoutine,
  deleteRoutineFolder,
  deleteSet,
  deleteWorkout,
  duplicateLastSet,
  type ExerciseHistorySession,
  type ExercisePRs,
  type ExerciseSearchParams,
  getBodyMeasurements,
  // Stats
  getCalendarData,
  getDashboardStats,
  getExercise,
  getExerciseCounts,
  getExerciseHistory,
  getExercisePRs,
  getExercises,
  getPersonalRecords,
  // Settings
  getPlateConfig,
  getPreviousWorkoutSets,
  getProgressSummary,
  getRoutineFolders,
  getRoutines,
  getRoutineWithExercises,
  getSystemRoutines,
  getUserSettings,
  getVolumeByMuscleGroup,
  getWeeklyVolumeTrend,
  getWeightHistory,
  getWorkout,
  getWorkoutHistory,
  getWorkoutWithDetails,
  type PlateConfig,
  type PRListItem,
  type PreviousSetData,
  type ProgressSummary,
  type RoutineListItem,
  type RoutineWithExercises,
  removeExerciseFromWorkout,
  type SaveRoutineInput,
  type StartWorkoutInput,
  saveRoutine,
  startWorkout,
  type UpdateRoutineInput,
  type UpdateSetInput,
  type UpdateSettingsInput,
  type UpdateWorkoutInput,
  type UpsertMeasurementInput,
  type UserSettings,
  updateRoutine,
  updateSet,
  updateUserSettings,
  updateWorkout,
  upsertMeasurement,
  type VolumeByMuscle,
  type WeeklyVolume,
  type WeightHistoryPoint,
  type WorkoutHistoryItem,
  type WorkoutHistoryParams,
  type WorkoutWithDetails,
} from "@/actions/weight-training";

// ════════════════════════════════════════════════════════════════════════════
// QUERY KEYS (imported from centralized file)
// ════════════════════════════════════════════════════════════════════════════

import { useAuthState } from "@/hooks/use-auth-state";
// Import for local use in hooks
import { wtKeys } from "@/lib/weight-training-keys";

// Re-export for consumers of this module
export { wtKeys };

// ════════════════════════════════════════════════════════════════════════════
// SETTINGS HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useSettings() {
  const accountId = useAuthState().identity?.id;
  return useQuery({
    queryKey: wtKeys.settings(accountId),
    queryFn: () => getUserSettings(),
    enabled: Boolean(accountId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function usePlateConfig() {
  return useQuery({
    queryKey: wtKeys.plateConfig(),
    queryFn: () => getPlateConfig(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  const accountId = useAuthState().identity?.id;
  const queryKey = wtKeys.settings(accountId);

  return useMutation({
    mutationKey: ["wt", "updateSettings"],
    mutationFn: (input: UpdateSettingsInput) => updateUserSettings(input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<UserSettings>(queryKey);
      queryClient.setQueryData<UserSettings>(queryKey, (old) =>
        old ? { ...old, ...input } : old,
      );
      return { prev, queryKey };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(ctx.queryKey, ctx.prev);
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      queryClient.invalidateQueries({
        queryKey: context?.queryKey ?? queryKey,
      });
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// EXERCISE HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useExercises(params?: ExerciseSearchParams) {
  return useQuery({
    queryKey: wtKeys.exercises(params),
    queryFn: () => getExercises(params),
    staleTime: 60 * 1000, // 1 minute
    placeholderData: keepPreviousData,
  });
}

export function useExerciseCounts() {
  return useQuery({
    queryKey: wtKeys.exerciseCounts(),
    queryFn: () => getExerciseCounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes - counts don't change often
  });
}

export function useInfiniteExercises(
  params?: Omit<ExerciseSearchParams, "offset">,
) {
  type ExercisePage = Awaited<ReturnType<typeof getExercises>>;
  return useInfiniteQuery({
    queryKey: ["wt", "exercises", "infinite", params] as const,
    queryFn: ({ pageParam = 0 }) =>
      getExercises({
        ...params,
        offset: pageParam,
        limit: params?.limit ?? 30,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: ExercisePage, allPages: ExercisePage[]) => {
      if (!lastPage.hasMore) return undefined;
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.exercises.length,
        0,
      );
      return totalLoaded;
    },
    staleTime: 60 * 1000,
  });
}

export function useExercise(id: string, isCustom = false) {
  return useQuery({
    queryKey: wtKeys.exercise(id),
    queryFn: () => getExercise(id, isCustom),
    enabled: !!id,
  });
}

export function useExerciseHistory(id: string, isCustom: boolean) {
  return useQuery({
    queryKey: wtKeys.exerciseHistory(id),
    queryFn: () => getExerciseHistory(id, isCustom),
    enabled: !!id,
  });
}

export function useExercisePRs(id: string, isCustom: boolean) {
  return useQuery({
    queryKey: wtKeys.exercisePRs(id),
    queryFn: () => getExercisePRs(id, isCustom),
    enabled: !!id,
  });
}

export function usePreviousSets(exerciseId: string, isCustom: boolean) {
  return useQuery({
    queryKey: wtKeys.previousSets(exerciseId, isCustom ? exerciseId : null),
    queryFn: () => getPreviousWorkoutSets(exerciseId, isCustom),
    enabled: !!exerciseId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useCreateCustomExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "createCustomExercise"],
    mutationFn: (input: CreateCustomExerciseInput) =>
      createCustomExercise(input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.exercises() });
      const prev = queryClient.getQueryData<{
        exercises: {
          id: string;
          name: string;
          primaryMuscle: string;
          equipment: string;
          isCustom: boolean;
        }[];
      }>(wtKeys.exercises());
      // Add optimistic exercise with temp ID
      const tempExercise = {
        id: `temp-${Date.now()}`,
        name: input.name,
        primaryMuscle: input.primaryMuscle,
        equipment: input.equipment,
        isCustom: true,
      };
      if (prev) {
        queryClient.setQueryData(wtKeys.exercises(), {
          ...prev,
          exercises: [...prev.exercises, tempExercise],
        });
      }
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.exercises(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.exercises() });
    },
  });
}

export function useDeleteCustomExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteCustomExercise"],
    mutationFn: (id: string) => deleteCustomExercise(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.exercises() });
      const prev = queryClient.getQueryData<{ exercises: { id: string }[] }>(
        wtKeys.exercises(),
      );
      queryClient.setQueryData<{ exercises: { id: string }[] }>(
        wtKeys.exercises(),
        (old) =>
          old
            ? { ...old, exercises: old.exercises.filter((e) => e.id !== id) }
            : old,
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.exercises(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.exercises() });
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// WORKOUT HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useWorkoutHistory(params?: WorkoutHistoryParams) {
  return useQuery({
    queryKey: wtKeys.workouts(params),
    queryFn: () => getWorkoutHistory(params),
    staleTime: 30 * 1000,
  });
}

export function useWorkout(id: string) {
  return useQuery({
    queryKey: wtKeys.workout(id),
    queryFn: () => getWorkout(id),
    enabled: !!id,
  });
}

export function useWorkoutWithDetails(id: string) {
  return useQuery({
    queryKey: wtKeys.workoutDetails(id),
    queryFn: () => getWorkoutWithDetails(id),
    enabled: !!id,
  });
}

export function useStartWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "startWorkout"],
    mutationFn: (input?: StartWorkoutInput) => startWorkout(input),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: wtKeys.workouts() });
      const prev = queryClient.getQueryData<{ workouts: WorkoutHistoryItem[] }>(
        wtKeys.workouts(),
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.workouts(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.workouts() });
    },
  });
}

export function useCompleteWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "completeWorkout"],
    mutationFn: (workoutId: string) => completeWorkout(workoutId),
    onMutate: async (workoutId) => {
      await queryClient.cancelQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
      const prev = queryClient.getQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
      );
      // Optimistically mark as completed
      if (prev) {
        queryClient.setQueryData(wtKeys.workoutDetails(workoutId), {
          ...prev,
          status: "completed",
          completedAt: new Date().toISOString(),
        });
      }
      return { prev, workoutId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          wtKeys.workoutDetails(ctx.workoutId),
          ctx.prev,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.workouts() });
      queryClient.invalidateQueries({ queryKey: wtKeys.dashboardStats() });
    },
  });
}

export function useDeleteWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteWorkout"],
    mutationFn: (id: string) => deleteWorkout(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.workouts() });
      const prev = queryClient.getQueryData<{ workouts: WorkoutHistoryItem[] }>(
        wtKeys.workouts(),
      );
      queryClient.setQueryData<{ workouts: WorkoutHistoryItem[] }>(
        wtKeys.workouts(),
        (old) =>
          old
            ? { ...old, workouts: old.workouts.filter((w) => w.id !== id) }
            : old,
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.workouts(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.workouts() });
      queryClient.invalidateQueries({ queryKey: wtKeys.dashboardStats() });
    },
  });
}

export function useUpdateWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "updateWorkout"],
    mutationFn: ({
      workoutId,
      input,
    }: {
      workoutId: string;
      input: UpdateWorkoutInput;
    }) => updateWorkout(workoutId, input),
    onMutate: async ({ workoutId, input }) => {
      await queryClient.cancelQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
      const prev = queryClient.getQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
      );
      queryClient.setQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
        (old) => (old ? { ...old, ...input } : old),
      );
      return { prev, workoutId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          wtKeys.workoutDetails(ctx.workoutId),
          ctx.prev,
        );
      }
    },
    onSettled: (_, __, { workoutId }) => {
      queryClient.invalidateQueries({ queryKey: wtKeys.workout(workoutId) });
      queryClient.invalidateQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
    },
  });
}

export function useAddExerciseToWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "addExerciseToWorkout"],
    mutationFn: ({
      workoutId,
      input,
    }: {
      workoutId: string;
      input: AddExerciseToWorkoutInput;
    }) => addExerciseToWorkout(workoutId, input),
    onMutate: async ({ workoutId, input }) => {
      await queryClient.cancelQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
      const prev = queryClient.getQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
      );
      // Add optimistic exercise with temp ID
      const tempExercise = {
        id: `temp-${Date.now()}`,
        exerciseId: input.exerciseId ?? null,
        customExerciseId: input.customExerciseId ?? null,
        exerciseName: "Loading...",
        orderIndex: prev?.exercises.length ?? 0,
        supersetGroup: null,
        notes: null,
        totalVolume: null,
        sets: [],
      };
      if (prev) {
        queryClient.setQueryData(wtKeys.workoutDetails(workoutId), {
          ...prev,
          exercises: [...prev.exercises, tempExercise],
        });
      }
      return { prev, workoutId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          wtKeys.workoutDetails(ctx.workoutId),
          ctx.prev,
        );
      }
    },
    onSettled: (_, __, { workoutId }) => {
      queryClient.invalidateQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
    },
  });
}

export function useRemoveExerciseFromWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "removeExerciseFromWorkout"],
    mutationFn: ({
      workoutId,
      exerciseId,
    }: {
      workoutId: string;
      exerciseId: string;
    }) => removeExerciseFromWorkout(workoutId, exerciseId),
    onMutate: async ({ workoutId, exerciseId }) => {
      await queryClient.cancelQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
      const prev = queryClient.getQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
      );
      queryClient.setQueryData<WorkoutWithDetails>(
        wtKeys.workoutDetails(workoutId),
        (old) =>
          old
            ? {
                ...old,
                exercises: old.exercises.filter((e) => e.id !== exerciseId),
              }
            : old,
      );
      return { prev, workoutId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          wtKeys.workoutDetails(ctx.workoutId),
          ctx.prev,
        );
      }
    },
    onSettled: (_, __, { workoutId }) => {
      queryClient.invalidateQueries({
        queryKey: wtKeys.workoutDetails(workoutId),
      });
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// SET HOOKS (with optimistic updates)
// ════════════════════════════════════════════════════════════════════════════

export function useAddSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "addSet"],
    mutationFn: ({
      workoutExerciseId,
      input,
    }: {
      workoutExerciseId: string;
      input?: AddSetInput;
    }) => addSet(workoutExerciseId, input),
    onMutate: async () => {
      // Cancel any outgoing workout queries
      await queryClient.cancelQueries({ queryKey: [...wtKeys.all, "workout"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...wtKeys.all, "workout"],
      });
    },
  });
}

export function useCompleteSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "completeSet"],
    mutationFn: (setId: string) => completeSet(setId),
    onMutate: async () => {
      // Cancel any outgoing queries
      await queryClient.cancelQueries({ queryKey: [...wtKeys.all, "workout"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...wtKeys.all, "workout"],
      });
      queryClient.invalidateQueries({
        queryKey: wtKeys.dashboardStats(),
      });
    },
  });
}

export function useUpdateSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "updateSet"],
    mutationFn: ({ setId, input }: { setId: string; input: UpdateSetInput }) =>
      updateSet(setId, input),
    onMutate: async () => {
      // Cancel any outgoing queries to prevent overwrites
      await queryClient.cancelQueries({ queryKey: [...wtKeys.all, "workout"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...wtKeys.all, "workout"],
      });
    },
  });
}

export function useDeleteSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteSet"],
    mutationFn: (setId: string) => deleteSet(setId),
    onMutate: async () => {
      // Cancel any outgoing queries
      await queryClient.cancelQueries({ queryKey: [...wtKeys.all, "workout"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...wtKeys.all, "workout"],
      });
    },
  });
}

export function useDuplicateSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "duplicateSet"],
    mutationFn: (workoutExerciseId: string) =>
      duplicateLastSet(workoutExerciseId),
    onMutate: async () => {
      // Cancel any outgoing queries
      await queryClient.cancelQueries({ queryKey: [...wtKeys.all, "workout"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...wtKeys.all, "workout"],
      });
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// ROUTINE HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useRoutines() {
  return useQuery({
    queryKey: wtKeys.routines(),
    queryFn: () => getRoutines(),
    staleTime: 60 * 1000,
  });
}

export function useSystemRoutines() {
  return useQuery({
    queryKey: wtKeys.systemRoutines(),
    queryFn: () => getSystemRoutines(),
    staleTime: 5 * 60 * 1000, // System routines change rarely
  });
}

export function useRoutineFolders() {
  return useQuery({
    queryKey: wtKeys.routineFolders(),
    queryFn: () => getRoutineFolders(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateRoutineFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "createRoutineFolder"],
    mutationFn: (name: string) => createRoutineFolder(name),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routineFolders() });
    },
  });
}

export function useDeleteRoutineFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteRoutineFolder"],
    mutationFn: (folderId: string) => deleteRoutineFolder(folderId),
    onSettled: () => {
      // Routines inside the folder move back to the root list.
      queryClient.invalidateQueries({ queryKey: wtKeys.routineFolders() });
      queryClient.invalidateQueries({ queryKey: wtKeys.routines() });
    },
  });
}

export function useRoutine(id: string) {
  return useQuery({
    queryKey: wtKeys.routine(id),
    queryFn: () => getRoutineWithExercises(id),
    enabled: !!id,
  });
}

export function useCreateRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "createRoutine"],
    mutationFn: (input: CreateRoutineInput) => createRoutine(input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.routines() });
      const prev = queryClient.getQueryData<RoutineListItem[]>(
        wtKeys.routines(),
      );
      // Add optimistic routine with temp ID
      const tempRoutine: RoutineListItem = {
        id: `temp-${Date.now()}`,
        name: input.name,
        description: input.description ?? null,
        estimatedMinutes: input.estimatedMinutes ?? null,
        exerciseCount: 0,
        folderId: null,
        timesUsed: 0,
        lastUsedAt: null,
        previewImageUrls: [],
        isPublic: false,
      };
      queryClient.setQueryData<RoutineListItem[]>(wtKeys.routines(), (old) =>
        old ? [...old, tempRoutine] : [tempRoutine],
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.routines(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routines() });
    },
  });
}

export function useUpdateRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "updateRoutine"],
    mutationFn: ({
      routineId,
      input,
    }: {
      routineId: string;
      input: UpdateRoutineInput;
    }) => updateRoutine(routineId, input),
    onMutate: async ({ routineId, input }) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.routine(routineId) });
      await queryClient.cancelQueries({ queryKey: wtKeys.routines() });
      const prevRoutine = queryClient.getQueryData<RoutineWithExercises>(
        wtKeys.routine(routineId),
      );
      const prevRoutines = queryClient.getQueryData<RoutineListItem[]>(
        wtKeys.routines(),
      );
      // Update the routine detail
      queryClient.setQueryData<RoutineWithExercises>(
        wtKeys.routine(routineId),
        (old) => (old ? { ...old, ...input } : old),
      );
      // Update the routines list
      queryClient.setQueryData<RoutineListItem[]>(wtKeys.routines(), (old) =>
        old?.map((r) => (r.id === routineId ? { ...r, ...input } : r)),
      );
      return { prevRoutine, prevRoutines, routineId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prevRoutine) {
        queryClient.setQueryData(
          wtKeys.routine(ctx.routineId),
          ctx.prevRoutine,
        );
      }
      if (ctx?.prevRoutines) {
        queryClient.setQueryData(wtKeys.routines(), ctx.prevRoutines);
      }
    },
    onSettled: (_, __, { routineId }) => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routine(routineId) });
      queryClient.invalidateQueries({ queryKey: wtKeys.routines() });
    },
  });
}

export function useDeleteRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteRoutine"],
    mutationFn: (id: string) => deleteRoutine(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.routines() });
      const prev = queryClient.getQueryData<RoutineListItem[]>(
        wtKeys.routines(),
      );
      queryClient.setQueryData<RoutineListItem[]>(wtKeys.routines(), (old) =>
        old?.filter((r) => r.id !== id),
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.routines(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routines() });
    },
  });
}

export function useAddExerciseToRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "addExerciseToRoutine"],
    mutationFn: ({
      routineId,
      input,
    }: {
      routineId: string;
      input: AddExerciseToRoutineInput;
    }) => addExerciseToRoutine(routineId, input),
    onMutate: async ({ routineId }) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.routine(routineId) });
      const prev = queryClient.getQueryData<RoutineWithExercises>(
        wtKeys.routine(routineId),
      );
      // Since we don't have full exercise info, just increment the count optimistically
      // The actual exercise will be added when onSettled invalidates
      return { prev, routineId };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.routine(ctx.routineId), ctx.prev);
      }
    },
    onSettled: (_, __, { routineId }) => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routine(routineId) });
    },
  });
}

export function useSaveRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "saveRoutine"],
    mutationFn: (input: SaveRoutineInput) => saveRoutine(input),
    onMutate: async (input) => {
      // Cancel queries
      await queryClient.cancelQueries({ queryKey: wtKeys.routines() });
      if (input.id) {
        await queryClient.cancelQueries({ queryKey: wtKeys.routine(input.id) });
      }

      // Snapshot previous data
      const prevRoutines = queryClient.getQueryData<RoutineListItem[]>(
        wtKeys.routines(),
      );
      const prevRoutine = input.id
        ? queryClient.getQueryData<RoutineWithExercises>(
            wtKeys.routine(input.id),
          )
        : undefined;

      // Optimistic update for list
      if (prevRoutines) {
        if (input.id) {
          // Update existing
          queryClient.setQueryData<RoutineListItem[]>(
            wtKeys.routines(),
            prevRoutines.map((r) =>
              r.id === input.id
                ? {
                    ...r,
                    name: input.name,
                    description: input.description ?? null,
                    estimatedMinutes: input.estimatedMinutes ?? null,
                    folderId: input.folderId ?? null,
                    exerciseCount: input.exercises.length,
                    lastUsedAt: new Date().toISOString(), // Bump to top
                    previewImageUrls: r.previewImageUrls ?? [],
                  }
                : r,
            ),
          );
        } else {
          // Add new
          const tempRoutine: RoutineListItem = {
            id: `temp-${Date.now()}`,
            name: input.name,
            description: input.description ?? null,
            estimatedMinutes: input.estimatedMinutes ?? null,
            folderId: input.folderId ?? null,
            timesUsed: 0,
            lastUsedAt: new Date().toISOString(),
            exerciseCount: input.exercises.length,
            previewImageUrls: [],
            isPublic: false,
          };
          queryClient.setQueryData<RoutineListItem[]>(wtKeys.routines(), [
            tempRoutine,
            ...prevRoutines,
          ]);
        }
      }

      // Optimistic update for detail
      if (input.id && prevRoutine) {
        queryClient.setQueryData<RoutineWithExercises>(
          wtKeys.routine(input.id),
          {
            ...prevRoutine,
            name: input.name,
            description: input.description ?? null,
            estimatedMinutes: input.estimatedMinutes ?? null,
            folderId: input.folderId ?? null,
            exercises: input.exercises.map((e, index) => ({
              id: `temp-ex-${index}`, // Temporary ID
              exerciseId: e.exerciseId ?? null,
              customExerciseId: e.customExerciseId ?? null,
              exerciseName: "Loading...", // Ideally we'd pass names too but input doesn't have them
              orderIndex: index,
              targetSets: e.targetSets ?? 3,
              targetReps: e.targetReps ?? null,
              targetWeight: e.targetWeight ?? null,
              restSeconds: e.restSeconds ?? null,
              supersetGroup: e.supersetGroup ?? null,
              notes: e.notes ?? null,
              primaryMuscle: null,
              equipment: null,
              imageUrl: null,
              gifUrl: null,
            })),
          },
        );
      }

      return { prevRoutines, prevRoutine };
    },
    onError: (_, input, ctx) => {
      if (ctx?.prevRoutines) {
        queryClient.setQueryData(wtKeys.routines(), ctx.prevRoutines);
      }
      if (input.id && ctx?.prevRoutine) {
        queryClient.setQueryData(wtKeys.routine(input.id), ctx.prevRoutine);
      }
    },
    onSettled: (_, __, input) => {
      queryClient.invalidateQueries({ queryKey: wtKeys.routines() });
      if (input.id) {
        queryClient.invalidateQueries({ queryKey: wtKeys.routine(input.id) });
      }
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// STATS HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useDashboardStats() {
  return useQuery({
    queryKey: wtKeys.dashboardStats(),
    queryFn: () => getDashboardStats(),
    staleTime: 60 * 1000,
  });
}

export function usePersonalRecords(limit?: number) {
  return useQuery({
    queryKey: wtKeys.personalRecords(limit),
    queryFn: () => getPersonalRecords(limit),
    staleTime: 60 * 1000,
  });
}

export function useVolumeByMuscle(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: wtKeys.volumeByMuscle(),
    queryFn: () => getVolumeByMuscleGroup(startDate, endDate),
    staleTime: 60 * 1000,
  });
}

export function useWeeklyVolume(weeks?: number) {
  return useQuery({
    queryKey: wtKeys.weeklyVolume(weeks),
    queryFn: () => getWeeklyVolumeTrend(weeks),
    staleTime: 60 * 1000,
  });
}

export function useCalendarData(year: number, month: number) {
  return useQuery({
    queryKey: wtKeys.calendar(year, month),
    queryFn: () => getCalendarData(year, month),
    staleTime: 5 * 60 * 1000,
  });
}

// ════════════════════════════════════════════════════════════════════════════
// BODY MEASUREMENT HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useBodyMeasurements() {
  return useQuery({
    queryKey: wtKeys.bodyMeasurements(),
    queryFn: () => getBodyMeasurements(),
    staleTime: 60 * 1000,
  });
}

export function useProgressSummary() {
  return useQuery({
    queryKey: wtKeys.progressSummary(),
    queryFn: () => getProgressSummary(),
    staleTime: 60 * 1000,
  });
}

export function useWeightHistory(limit?: number) {
  return useQuery({
    queryKey: wtKeys.weightHistory(limit),
    queryFn: () => getWeightHistory(limit),
    staleTime: 60 * 1000,
  });
}

export function useUpsertMeasurement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "upsertMeasurement"],
    mutationFn: (input: UpsertMeasurementInput) => upsertMeasurement(input),
    onMutate: async () => {
      // Cancel any outgoing queries
      await queryClient.cancelQueries({ queryKey: wtKeys.bodyMeasurements() });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.bodyMeasurements() });
      queryClient.invalidateQueries({ queryKey: wtKeys.progressSummary() });
      queryClient.invalidateQueries({ queryKey: wtKeys.weightHistory() });
    },
  });
}

export function useDeleteMeasurement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wt", "deleteMeasurement"],
    mutationFn: (id: string) => deleteMeasurement(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: wtKeys.bodyMeasurements() });
      const prev = queryClient.getQueryData<BodyMeasurementItem[]>(
        wtKeys.bodyMeasurements(),
      );
      queryClient.setQueryData<BodyMeasurementItem[]>(
        wtKeys.bodyMeasurements(),
        (old) => old?.filter((m) => m.id !== id),
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(wtKeys.bodyMeasurements(), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wtKeys.bodyMeasurements() });
      queryClient.invalidateQueries({ queryKey: wtKeys.progressSummary() });
      queryClient.invalidateQueries({ queryKey: wtKeys.weightHistory() });
    },
  });
}

// ════════════════════════════════════════════════════════════════════════════
// RE-EXPORTS FOR CONVENIENCE
// ════════════════════════════════════════════════════════════════════════════

export type {
  BodyMeasurementItem,
  CalendarDay,
  CompleteSetResult,
  DashboardStats,
  ExerciseHistorySession,
  ExercisePRs,
  ExerciseSearchParams,
  PlateConfig,
  PRListItem,
  PreviousSetData,
  ProgressSummary,
  RoutineListItem,
  RoutineWithExercises,
  UserSettings,
  VolumeByMuscle,
  WeeklyVolume,
  WeightHistoryPoint,
  WorkoutHistoryItem,
  WorkoutWithDetails,
};
